import type { DashboardLanguage } from "../locale";
import type { TemplateVariable, TemplateContext } from "../../template";
import { effectiveTemplateVariables } from "../../template";
import { SYSTEM_TEMPLATE_VARIABLE_LIST } from "../../template-variables";
import { channelVariableTemplateDescription, systemTemplateVariableLocale } from "../locale";
import { templateVariableParameter, type TemplateTextFieldGroupPresentation } from "./template-text-field-model";
import type { TemplateTextFieldVariable } from "./TemplateTextField";

export interface PanelChannelVariable {
  name: string;
  value: number;
  description: string;
}

interface ModuleTemplateVariableGroupCopy {
  label: Readonly<Record<DashboardLanguage, string>>;
  icon: { paths: readonly string[] };
  order?: number;
}

interface RegisteredModuleTemplateVariable {
  moduleId: string;
  pickerGroup?: ModuleTemplateVariableGroupCopy & { id: string };
}

const moduleTemplateFieldGroup = (
  id: string,
  group: ModuleTemplateVariableGroupCopy | undefined,
  language: DashboardLanguage,
): TemplateTextFieldGroupPresentation | undefined => group === undefined ? undefined : ({
  id,
  label: group.label[language],
  iconPaths: group.icon.paths,
  order: group.order ?? 500,
});

export const registeredModuleTemplateGroup = (
  variable: RegisteredModuleTemplateVariable,
  language: DashboardLanguage,
): TemplateTextFieldGroupPresentation | undefined => moduleTemplateFieldGroup(
  variable.pickerGroup?.id ?? variable.moduleId,
  variable.pickerGroup,
  language,
);

export const effectivePanelTemplateVariables = (
  context: TemplateContext,
  moduleVariables: readonly TemplateVariable[],
  channelVariables: readonly PanelChannelVariable[],
): TemplateVariable[] => effectiveTemplateVariables(
  context,
  moduleVariables,
  channelVariables.map((variable) => ({
    name: `var.${variable.name}`,
    group: "channel",
    sample: String(variable.value),
    maxLength: 10,
    source: "channel",
  })),
  SYSTEM_TEMPLATE_VARIABLE_LIST,
);

export const panelTemplateOptions = (
  context: TemplateContext,
  moduleVariables: readonly TemplateVariable[],
  channelVariables: readonly PanelChannelVariable[],
  language: DashboardLanguage,
  moduleDescriptions: readonly TemplateTextFieldVariable[] = [],
  moduleGroup?: { id: string; definition: ModuleTemplateVariableGroupCopy },
): TemplateTextFieldVariable[] => {
  const declarations = effectivePanelTemplateVariables(context, moduleVariables, channelVariables);
  const moduleByName = new Map(moduleDescriptions.map((variable) => [variable.name, variable]));
  const channelByName = new Map(channelVariables.map((variable) => [`var.${variable.name}`, variable]));
  return declarations.map((variable) => {
    const parameter = templateVariableParameter(variable.parameters, variable.parameterDefault);
    if (variable.source === "system") {
      const localized = systemTemplateVariableLocale[language][variable.name as keyof typeof systemTemplateVariableLocale.de];
      return {
        name: variable.name,
        label: localized.label,
        description: localized.description,
        sample: localized.sample,
        group: variable.group ?? "context",
        kind: "system",
        ...(variable.external === undefined ? {} : { external: variable.external }),
        ...(parameter === undefined ? {} : { parameter }),
      };
    }
    if (variable.source === "channel") {
      const channel = channelByName.get(variable.name);
      const localizedSample = new Intl.NumberFormat(language === "de" ? "de-DE" : "en-US", { maximumFractionDigits: 0 }).format(Number(variable.sample));
      return {
        name: variable.name,
        label: channel?.name ?? variable.name,
        description: channel?.description || channelVariableTemplateDescription[language],
        sample: localizedSample,
        group: "channel",
        kind: "channel",
      };
    }
    const localized = moduleByName.get(variable.name);
    const pickerCopy = variable.picker?.[language];
    const sourceGroup = moduleGroup === undefined
      ? undefined
      : moduleTemplateFieldGroup(moduleGroup.id, moduleGroup.definition, language);
    return {
      name: variable.name,
      label: pickerCopy?.label ?? localized?.label ?? variable.name,
      description: pickerCopy?.description ?? variable.localizedDescription?.[language] ?? localized?.description ?? variable.name,
      sample: localized?.sample ?? variable.sample,
      ...(pickerCopy?.sample === undefined ? {} : { pickerSample: pickerCopy.sample }),
      group: variable.group ?? "context",
      kind: "module",
      ...(sourceGroup === undefined ? {} : { sourceGroup }),
      ...(variable.external === undefined ? {} : { external: variable.external }),
      ...(variable.parameters === undefined ? {} : { parameters: variable.parameters }),
      ...(parameter === undefined ? {} : { parameter }),
    };
  });
};
